import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * A panel anchored to a trigger: the styled select's listbox, a filter pill's
 * options, the workspace switcher.
 *
 * Portal + `position: fixed` for the reason the row menu documents (D-078):
 * list panels clip their children and an animated page wrapper becomes the
 * containing block of anything fixed inside it. The panel opens below the
 * anchor, flips above when the viewport has no room, and follows the anchor
 * through scrolls (capture phase, so scrolling panes count too).
 *
 * Closing is the parent's call: an outside pointer-down or Escape asks for it
 * through `onClose`. Pointer-down rather than a click-catching backdrop, so a
 * click on another control both closes this panel and reaches that control.
 */

const GAP = 6;
const EDGE = 8;

interface Placement {
  top: number;
  left: number;
  minWidth: number;
  maxHeight: number;
}

function place(anchor: DOMRect, panelHeight: number, align: 'start' | 'end', width: number | undefined): Placement {
  const panelWidth = Math.max(width ?? 0, anchor.width);
  const preferredLeft = align === 'end' ? anchor.right - panelWidth : anchor.left;
  const left = Math.min(Math.max(EDGE, preferredLeft), Math.max(EDGE, window.innerWidth - panelWidth - EDGE));
  const spaceBelow = window.innerHeight - anchor.bottom - GAP - EDGE;
  const spaceAbove = anchor.top - GAP - EDGE;
  const below = panelHeight <= spaceBelow || spaceBelow >= spaceAbove;
  const maxHeight = Math.max(120, below ? spaceBelow : spaceAbove);
  const height = Math.min(panelHeight, maxHeight);
  const top = below ? anchor.bottom + GAP : anchor.top - GAP - height;
  return { top, left, minWidth: panelWidth, maxHeight };
}

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Minimum width in px; the panel is never narrower than its anchor. */
  width?: number;
  align?: 'start' | 'end';
  id?: string;
  role?: string;
  'aria-label'?: string;
  className?: string;
}

export function Popover({ anchorRef, open, onClose, children, width, align = 'start', id, role, className = '', ...aria }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const height = panelRef.current?.scrollHeight ?? 240;
    setPlacement(place(anchor.getBoundingClientRect(), height, align, width));
  }, [anchorRef, align, width]);

  useLayoutEffect(() => {
    if (open) reposition();
    else setPlacement(null);
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    // Window, capture phase: Escape closes this panel only. A modal around it
    // listens on the document and would otherwise close as well.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, onClose, reposition, anchorRef]);

  if (!open) return null;
  const style: CSSProperties = {
    top: placement?.top ?? 0,
    left: placement?.left ?? 0,
    minWidth: placement?.minWidth,
    maxHeight: placement?.maxHeight,
  };
  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role={role}
      aria-label={aria['aria-label']}
      style={style}
      // `nokey`: a key pressed in a menu is not for the builder canvas behind it (React Flow's Delete).
      className={`nokey fixed z-50 overflow-y-auto rounded-xl bg-(--color-surface) p-1.5 shadow-popover ring-1 ring-(--color-line) animate-fade-in ${
        placement ? '' : 'invisible'
      } ${className}`}
    >
      {children}
    </div>,
    document.body,
  );
}
